import React, { useState } from 'react';
import { PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { DollarSign, Users, Database, TrendingUp, Loader2, AlertCircle } from 'lucide-react';
import MetricCard from '../components/MetricCard';
import DateRangePicker from '../components/DateRangePicker';
import { 
  formatCurrency,
  getTotalCostsFromData,
  getDailyCostsFromData,
  getOverallMetricsFromData,
  getTopUsersFromData
} from '../utils/dataProcessor';
import { useDateRange } from '../contexts/DateRangeContext';
import { useCostDataWithFallback } from '../hooks/useCostData';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const Overview: React.FC = () => {
  const { dateRange } = useDateRange();
  const [showProvidersSeparately, setShowProvidersSeparately] = useState<boolean>(true);
  
  // React Query 훅 사용 - 날짜 범위 변경 시 자동으로 새 데이터 가져오기
  const { data: costData, isLoading, isError, error } = useCostDataWithFallback({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    enabled: Boolean(dateRange.startDate && dateRange.endDate)
  });

  console.log('🔄 Overview 컴포넌트 렌더링:', {
    dateRange,
    isLoading,
    isError,
    dataCount: costData?.length || 0
  });

  // 로딩 상태 처리
  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SaaSポートフォリオ</h1>
          <p className="text-gray-600">SaaSポートフォリオの概要、アプリの内訳、使用状況。</p>
        </div>

        {/* Date Range Picker */}
        <DateRangePicker />

        {/* Loading Content */}
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-gray-600">Loading...</h2>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태 처리
  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 font-medium mb-2">데이터 로드 중 오류가 발생했습니다</p>
          <p className="text-gray-600 text-sm">
            {error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다'}
          </p>
        </div>
      </div>
    );
  }

  // 데이터가 없는 경우
  if (!costData || costData.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Database className="h-8 w-8 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">해당 기간에 데이터가 없습니다</p>
          <p className="text-gray-500 text-sm mt-2">다른 날짜 범위를 선택해보세요</p>
        </div>
      </div>
    );
  }

  // 새로운 함수들을 사용하여 데이터 처리
  const metrics = getOverallMetricsFromData(costData);
  const providerData = getTotalCostsFromData(costData);
  const dailyCosts = getDailyCostsFromData(costData);
  const topUsers = getTopUsersFromData(costData, 5);

  console.log('📊 처리된 데이터:', {
    metrics,
    providerCount: providerData.length,
    dailyCount: dailyCosts.length,
    topUsersCount: topUsers.length
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">SaaSポートフォリオ</h1>
        <p className="text-gray-600">SaaSポートフォリオの概要、アプリの内訳、使用状況。</p>
      </div>

      {/* Date Range Picker */}
      <DateRangePicker />

      {/* Data Info Badge */}
      <div className="flex items-center space-x-2">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          📊 {costData.length}개 항목
        </span>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          📅 {dateRange.startDate} ~ {dateRange.endDate}
        </span>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="総コスト"
          value={formatCurrency(metrics.totalCost)}
          icon={DollarSign}
          color="blue"
          change={{ value: 12.5, type: 'increase' }}
        />
        <MetricCard
          title="アクティブユーザー"
          value={metrics.uniqueUsers}
          icon={Users}
          color="green"
          change={{ value: 3.2, type: 'increase' }}
        />
        {/* <MetricCard
          title="SaaS Providers"
          value={metrics.uniqueProviders}
          icon={Database}
          color="purple"
        /> */}
        <MetricCard
          title="ユーザーあたりの平均コスト"
          value={formatCurrency(metrics.avgCostPerUser)}
          icon={TrendingUp}
          color="orange"
          change={{ value: 8.1, type: 'decrease' }}
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 gap-6">
        {/* Provider Distribution */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">プロバイダー別のコスト</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={providerData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ provider, totalCost }) => `${provider}: ${formatCurrency(totalCost)}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="totalCost"
              >
                {providerData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value as number)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {providerData.map((provider, index) => (
              <div key={provider.provider} className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="font-medium">{provider.provider}</span>
                  <span className="text-gray-500">{provider.userCount} ユーザー</span>
                </div>
                <span className="font-semibold">{formatCurrency(provider.totalCost)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Usage Trend */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">コストトレンド</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">合計</span>
              <button
                onClick={() => setShowProvidersSeparately(!showProvidersSeparately)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showProvidersSeparately ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showProvidersSeparately ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-600">個別</span>
            </div>
          </div>
          
          {(() => {
            if (showProvidersSeparately) {
              return (
                <div>
                  {/* 범례 */}
                  <div className="flex flex-wrap gap-4 mb-4">
                    {providerData.map((provider, index) => (
                      <div key={provider.provider} className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        ></div>
                        <span className="text-sm text-gray-600">{provider.provider}</span>
                      </div>
                    ))}
                  </div>
                  
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailyCosts}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis tickFormatter={(value) => `$${value}`} />
                      <Tooltip
                        formatter={(value, name) => {
                          const provider = providerData.find(p => p.provider === name);
                          return [formatCurrency(value as number), provider?.provider || name];
                        }}
                        labelFormatter={(label) => new Date(label).toLocaleDateString('en-US')}
                      />
                      {providerData.map((provider, index) => (
                        <Line 
                          key={provider.provider}
                          type="monotone" 
                          dataKey={provider.provider} 
                          stroke={COLORS[index % COLORS.length]} 
                          strokeWidth={2}
                          dot={{ fill: COLORS[index % COLORS.length], strokeWidth: 2, r: 3 }}
                          activeDot={{ r: 5, fill: COLORS[index % COLORS.length] }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              );
            } else {
              return (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dailyCosts}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis tickFormatter={(value) => `$${value}`} />
                    <Tooltip
                      formatter={(value) => formatCurrency(value as number)}
                      labelFormatter={(label) => new Date(label).toLocaleDateString('en-US')}
                    />
                    <Line type="monotone" dataKey="totalCost" stroke="#3b82f6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              );
            }
          })()}
        </div>
      </div>
      

      {/* Top Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">コスト別トップユーザー</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-900">ユーザー</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">総コスト</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">プロバイダー</th>
                <th className="text-right py-3 px-4 font-medium text-gray-900">使用状況の分布</th>
              </tr>
            </thead>
            <tbody>
              {topUsers.map((user, index) => (
                <tr key={user.identifier} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600">
                          {user.identifier.split('@')[0].charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.identifier.split('@')[0]}</p>
                        <p className="text-sm text-gray-500">{user.identifier}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 font-semibold text-gray-900">
                    {formatCurrency(user.totalCost)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex space-x-1">
                      {Object.keys(user.providers).map((provider, idx) => (
                        <span
                          key={provider}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {provider}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex justify-end space-x-2">
                      {Object.entries(user.providers).map(([provider, cost], idx) => (
                        <div key={provider} className="text-right">
                          <p className="text-sm font-medium text-gray-900">{formatCurrency(cost)}</p>
                          <p className="text-xs text-gray-500">{provider}</p>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Overview;